// const _ = require('lodash')
const range = require('lodash/range')
const isEmpty = require('lodash/isEmpty')
const chunk = require('lodash/chunk')
const concat = require('lodash/concat')
const find = require('lodash/find')

const qs = require('qs')
const axios = require('axios')
const Cache = require('./cache')

const { unpackSpecifiers, preserveQuotes, convertResourceToFindMethod } = require('./helpers')
const { sportEventConvert } = require('./formatters/v1')
const WebsocketListener = require('./websocket-listener')

const PAGE_SIZE = 5000

const resources = ['markets', 'outcomes', 'sports', 'players', 'competitors', 'categories', 'tournaments']

const usersToShortMap = {
  winlineby: 'by',
  betone: 'bo',
  winlinekz: 'kz',
  winin: 'in',
  orbitbet: 'ng'
}

class SportsbookClientLite {
  /**
   * Constructor
   * @param {String} baseUrl
   * @param {String} apiKey
   */
  constructor(baseUrl, apiKey, { user, appId = 'unknown', extraResources = [], axiosTimeout = 150000 } = {}) {
    this.apiKey = apiKey
    this.axios = axios.create({
      baseURL: baseUrl,
      timeout: axiosTimeout,
      headers: {
        'x-application-id': appId,
        'x-access-token': apiKey
      }
    })

    this.user = user

    this.supportedLangs = ['ru', 'en', 'kz']
    this.supportedSportEventTypes = ['match', 'stage', 'season', 'tournament']
    this.supportedSeTypes = ['match', 'outright', 'race']

    this.cache = new Cache()
    this.cacheInitted = false

    this.counters = {
      getMarketVariants: 0,
      getMarketVariantsOutcomes: 0,
      getPlayer: 0
    }

    this.resources = resources.concat(extraResources)

    this.resources.forEach(resource => {
      const method = convertResourceToFindMethod(resource)

      this[method] = (params = {}) => this.find({ entityType: resource, ...params })

      const methodAll = `${method}All`

      this[methodAll] = (params = {}) => this.findAll({ entityType: resource, ...params })
    })
  }

  checkDate(date) {
    if (!(date instanceof Date)) {
      throw new Error('The object is not date')
    }
  }

  checkLang(lang) {
    const arrayLangs = Array.isArray(lang) ? lang : [lang]

    arrayLangs.forEach(langs => {
      if (!this.supportedLangs.includes(langs)) {
        throw new Error(`Not supported language: ${JSON.stringify({ langs, supportedLangs: this.supportedLangs })}`)
      }
    })

    return lang
  }

  checkSportEventType(sportEventType) {
    if (!sportEventType || !this.supportedSportEventTypes.includes(sportEventType)) {
      throw new Error(
        `Wrong sport event type: ${JSON.stringify({ sportEventType, sportEventTypes: this.supportedSportEventTypes })}`
      )
    }
  }

  checkSeType(seType) {
    if (!seType || !this.supportedSeTypes.includes(seType)) {
      throw new Error(`Wrong seType: ${JSON.stringify({ seType, seTypes: this.supportedSeTypes })}`)
    }
  }

  checkId(sportEventId) {
    if (sportEventId === undefined) {
      throw new Error('Empty id')
    }
  }

  checkActualWithProps(withProps) {
    withProps.forEach(singleWithProp => {
      if (
        ![
          'markets',
          'match',
          'match.venue',
          'tournament',
          'category',
          'sport',
          'activeMarketsCount',
          'competitors',
          'competitors.players'
        ].includes(singleWithProp)
      ) {
        throw new Error(`Wrong with field: ${singleWithProp}`)
      }
    })

    return true
  }

  /**
   * Load and save markets and outcomes to cache
   *
   * @param {Object} [params]
   * @param {String|String[]} [params.lang] lang for query
   * @param {String} [params.fallbackLang] fallbackLang for query
   * @returns {Promise<void>}
   */
  async init(params = {}) {
    const { lang, fallbackLang } = params
    const cacheInitted = this.cache.get('cacheInitted', 'cache')

    if (cacheInitted) {
      return
    }

    const pageSize = PAGE_SIZE

    const markets = []
    const outcomes = []

    const [firstMarkets, firstOutcomes] = await Promise.all([
      this.findMarkets({
        query: { provider: 'internal', variant: null, state: 1 },
        fields: ['id', 'name'],
        page: 1,
        pageSize,
        lang,
        fallbackLang
      }),
      this.findOutcomes({
        query: { provider: 'internal', state: 1 },
        fields: ['id', 'name'],
        page: 1,
        pageSize,
        lang,
        fallbackLang
      })
    ])

    const numMarketsPages = Math.ceil(firstMarkets.total / pageSize)
    const numOutcomesPages = Math.ceil(firstOutcomes.total / pageSize)

    markets.push(...firstMarkets.rows)
    outcomes.push(...firstOutcomes.rows)

    await Promise.all(
      range(1, numMarketsPages)
        .map(page => page + 1)
        .map(async page => {
          const { rows } = await this.findMarkets({
            query: { provider: 'internal', variant: null, state: 1 },
            fields: ['id', 'name'],
            page,
            pageSize,
            lang,
            fallbackLang
          })

          markets.push(...rows)
        })
    )

    await Promise.all(
      range(1, numOutcomesPages)
        .map(page => page + 1)
        .map(async page => {
          const { rows } = await this.findOutcomes({
            query: { provider: 'internal', state: 1 },
            fields: ['id', 'name'],
            page,
            pageSize,
            lang,
            fallbackLang
          })

          outcomes.push(...rows)
        })
    )

    this.cache.set('cacheInitted', true, 'cache', 24 * 60)

    if (lang) {
      markets.forEach(market => this.cache.set(market.id, { name: { [lang]: market.name } }, 'markets', 24 * 60))
      outcomes.forEach(outcome => this.cache.set(outcome.id, { name: { [lang]: outcome.name } }, 'outcomes', 24 * 60))

      return
    }

    markets.forEach(market => this.cache.set(market.id, { name: market.name }, 'markets', 24 * 60))
    outcomes.forEach(outcome => this.cache.set(outcome.id, { name: outcome.name }, 'outcomes', 24 * 60))
  }

  async count({ entityType, query }) {
    const uri = `${entityType}/count`

    let amount = null

    amount = (await this.axios.post(uri, { query })).data

    return amount
  }

  async find({
    entityType,
    query = {},
    page = 1,
    pageSize = 10,
    sort,
    fields,
    populate,
    search,
    searchFields = [],
    withQuery = {},
    withProps = [],
    lang,
    fallbackLang = 'en'
  }) {
    if (!entityType) {
      throw new Error(`Wrong entity type: ${entityType}`)
    }

    const _lang = lang ? this.checkLang(lang) : undefined

    const uri = `${entityType}/list`

    let result = null

    result = await this.axios.post(uri, {
      query,
      page,
      pageSize,
      sort,
      fields,
      populate,
      search,
      searchFields,
      withQuery,
      with: withProps,
      lang: _lang,
      fallbackLang
    })

    return result.data
  }

  async findAll({ entityType, query, fields, sort }) {
    const pageSize = PAGE_SIZE
    const entities = []

    const findMethod = convertResourceToFindMethod(entityType)
    const firstEntities = await this[findMethod]({
      query,
      fields,
      page: 1,
      pageSize,
      sort
    })

    entities.push(...firstEntities.rows)

    if (isEmpty(entities)) {
      return entities
    }

    const range = [...Array(firstEntities.totalPages - 1).keys()].map(k => k + 2)

    await Promise.all(
      range.map(async page => {
        const { rows } = await this[findMethod]({ query, fields, page, pageSize: firstEntities.pageSize, sort })

        entities.push(...rows)
      })
    )

    return entities
  }

  /**
   * The function returns amount of markets by query
   * @param {Object} [params]
   * @param {Object} [params.query]
   * @returns {Promise<*>}
   */
  async countMarkets(params) {
    const { query = {} } = params || {}

    return this.count({ entityType: 'markets', query })
  }

  /**
   * The function returns amount of sportEvents by query
   * @param {Object} [params]
   * @param {Object} [params.query]
   * @returns {Promise<*>}
   */
  async countSportEvents(params) {
    const { query = {} } = params

    return this.count({ entityType: 'sport_events', query })
  }

  async countOutcomes(params) {
    const { query = {} } = params || {}

    return this.count({ entityType: 'outcomes', query })
  }

  /**
   * The function returns amount of sports by query
   * @param [params]
   * @param [params.query]
   * @returns {Promise<*>}
   */
  async countSports(params) {
    const { query = {} } = params || {}

    return this.count({ entityType: 'sports', query })
  }

  async getActualTree({ userId, scheduled, lang, fallbackLang = 'en' } = {}) {
    const prefix = usersToShortMap[userId] || usersToShortMap[this.user]

    if (!prefix) {
      throw new Error('Unable to resolve actual prefix. Please provide userId or set user via constructor')
    }

    const uri = `${prefix}/actual/tree`
    let result = null

    const queryString = qs.stringify({ lang, fallbackLang, userId, scheduled })

    result = await this.axios.get(`${uri}?${queryString}`)

    return result.data
  }

  /**
   * The function returns single player.
   * @param {String} id
   * @param {String[]} [fields]=[]
   * @param {String[]} [withProps]=[]
   * @param {String|String[]} [lang]
   * @param {String} [fallbackLang]
   * @param {Object} [withQuery]={}
   * @returns {Promise<Object>}
   */
  async getPlayer({ id, fields, withQuery = {}, withProps = [], lang, fallbackLang = 'en' }) {
    ++this.counters.getPlayer
    const player = await this.get({
      entityType: 'players',
      withProps,
      withQuery,
      id,
      fields,
      lang,
      fallbackLang
    })

    return player
  }

  async insertPlayer({ entity, entities, returning }) {
    return this.insert({ entityType: 'players', entity, entities, returning })
  }

  async removePlayer({ id }) {
    return this.remove({ entityType: 'players', id })
  }

  async updatePlayer({ id, entity }) {
    return this.update({ entityType: 'players', id, entity })
  }

  /**
   * The function returns information about single sport event.
   * sb.getSportEvent({ id: 1 })
   * @param {Number} id
   * @param {String|String[]} [lang]
   * @param {String} [fallbackLang]
   * @param {String[]} [withProps]=[] - ['markets', 'activeMarkets', 'odds', 'competitors', 'match', 'stage', 'tournament', 'season']
   * @param {String[]} [fields]=[]
   * @param {Object} [withQuery]={} - example: { markets: { $in: [1, 2, 3] } }
   * @param {String} [userId]=null
   * @param {String} [version]=null - or 'v1'
   * @param {Boolean} [needResolve]=false
   * @param {Boolean} [throughActual]=true
   * @param {Boolean} [raw]=false - if true, returns real market status
   * @returns {Promise<Object>}
   */
  async getSportEvent({
    id,
    lang,
    fallbackLang = 'en',
    withProps = [],
    fields,
    withQuery = {},
    userId = null,
    version = null,
    needResolve = false,
    throughActual = true,
    raw = false
  }) {
    // const _lang = lang ? this.checkLang(lang) : undefined

    this.checkId(id)

    if (needResolve) {
      await this.init()
    }

    if (throughActual && (!withProps.length || this.checkActualWithProps(withProps))) {
      const resultFromActual = await this.findActualSportEvents({
        query: { id },
        lang,
        fallbackLang,
        withProps,
        withQuery,
        fields,
        userId,
        needResolve,
        stream: false,
        raw
      })

      if (resultFromActual.length) {
        return resultFromActual[0]
      }
    }

    if (
      withProps.includes('markets') &&
      !(withProps.includes('competitors') || withProps.includes('competitors.players'))
    ) {
      withProps.push('competitors')
    }

    let result = null

    result = await this.axios.post(`sport_events/${id}`, {
      with: withProps,
      fields,
      withQuery,
      userId,
      lang,
      fallbackLang,
      raw
    })

    const sportEvent = result.data

    if (needResolve) {
      await this.warmCache([sportEvent])
    }

    const resolvedSportEvent = needResolve ? await this.resolveMarkets(sportEvent, lang, fallbackLang) : sportEvent

    return version === 'v1' ? sportEventConvert(resolvedSportEvent) : resolvedSportEvent
  }

  /**
   * The function  returns actual sport events. It may be set several criteria via filters parameter.
   * sb.findActualSportEvents({ query: { $or: [{ id: 362, type: 'match'}, { id: 310, type: 'match'} ] }, withProps: ['match'], lang: 'ru', fields: ['id', 'match'] })
   * @param {Object} [params]
   * @param {String|String[]} [params.lang]
   * @param {String} [params.fallbackLang]
   * @param {Object} [params.query]- example: { $or: [{ id: 1, type: 'match' }, {id: 2, type: 'match'}] }
   * @param {String[]} [params.withProps]- ['markets', 'activeMarkets', 'odds', 'competitors', 'match', 'stage', 'tournament', 'season']
   * @param {String} [params.sort] - not implemented yet
   * @param {Number} [params.limit]
   * @param {String[]} [params.fields]=[]
   * @param {Object} [params.withQuery]={} - example: { markets: { $in: [1, 2, 3] } }
   * @param {String} [params.userId]=null
   * @param {String} [params.version]=null - or 'v1'
   * @param {String} [params.needResolve]=false
   * @param {String} [params.raw]=false
   * @returns {Promise<*>}
   */
  async findActualSportEvents(params) {
    const {
      query = {},
      lang,
      fallbackLang = 'en',
      sort,
      limit,
      fields,
      withQuery = {},
      withProps = [],
      userId,
      version = null,
      needResolve = false,
      raw = false
    } = params || {}

    if (needResolve) {
      await this.init()
    }

    if (
      withProps.includes('markets') &&
      !(withProps.includes('competitors') || withProps.includes('competitors.players'))
    ) {
      withProps.push('competitors')
    }

    const prefix = usersToShortMap[userId] || usersToShortMap[this.user]

    if (!prefix) {
      throw new Error('Unable to resolve actual prefix. Please provide userId or set user via constructor')
    }

    const uri = `${prefix}/actual`

    let result = null

    result = await this.axios.post(uri, {
      query,
      with: withProps,
      sort,
      limit,
      fields,
      userId,
      withQuery,
      lang,
      fallbackLang,
      raw
    })

    const sportEvents = result.data

    if (needResolve) {
      await this.warmCache(sportEvents)
    }

    const resolvedSportEvents = await Promise.all(
      sportEvents.map(sportEvent => (needResolve ? this.resolveMarkets(sportEvent, lang, fallbackLang) : sportEvent))
    )

    return version === 'v1'
      ? resolvedSportEvents.map(resolvedSportEvent => sportEventConvert(resolvedSportEvent))
      : resolvedSportEvents
  }

  /**
   * The function returns information about sport events by query.
   * sb.findSportEvents({ query: { $or: [{ id: 362, type: 'match'}, { id: 310, type: 'match'} ] }, withProps: ['match'], lang: 'ru', fields: ['id', 'match'] })
   * @params {Object} [params]
   * @param {String|String[]} [params.lang]
   * @param {String} [params.fallbackLang]
   * @param {Object} [params.query]- example: { $or: [{ id: 1, type: 'match' }, {id: 2, type: 'match'}] }
   * @param {String[]} [params.withProps]- ['markets', 'activeMarkets', 'odds', 'competitors', 'match', 'stage', 'tournament', 'season']
   * @param {String} [params.sort]- 'name'
   * @param {Number} [params.limit]
   * @param {Number} [params.offset]
   * @param {String[]} [params.fields]=[]
   * @param {Object} [params.withQuery]={} - example: { markets: { $in: [1, 2, 3] } }
   * @param {String} [params.version]=null - or 'v1'
   * @param {Boolean} [params.needResolve]=false
   * @param {Boolean} [params.raw]=false
   * @returns {Promise<Object>}
   */
  async findSportEvents(params) {
    const {
      query = {},
      page = 1,
      pageSize = 10,
      sort,
      fields,
      populate,
      search,
      searchFields = [],
      withQuery = {},
      withProps = [],
      lang,
      fallbackLang = 'en',
      version = null,
      needResolve = false,
      raw = false
    } = params || {}

    if (needResolve) {
      await this.init()
    }

    const result = await this.find({
      entityType: 'sport_events',
      query,
      page,
      pageSize,
      sort,
      fields,
      populate,
      search,
      searchFields,
      withQuery,
      withProps,
      lang,
      fallbackLang,
      raw
    })

    if (needResolve) {
      await this.warmCache(result.rows)
    }

    const resolvedSportEvents = await Promise.all(
      result.rows.map(sportEvent => (needResolve ? this.resolveMarkets(sportEvent, lang, fallbackLang) : sportEvent))
    )

    const resultSportEvents =
      version === 'v1'
        ? resolvedSportEvents.map(resolvedSportEvent => sportEventConvert(resolvedSportEvent))
        : resolvedSportEvents

    return Promise.resolve({ ...result, rows: resultSportEvents })
  }

  async get({ entityType, lang, fallbackLang = 'en', id, fields, withProps = [], withQuery = {} }) {
    if (!entityType) {
      throw new Error(`Wrong entity type: ${entityType}`)
    }

    const _lang = lang ? this.checkLang(lang) : undefined

    this.checkId(id)

    const uri = `${entityType}/${id}`

    let result = null

    result = await this.axios.post(uri, {
      id,
      with: withProps,
      fields,
      withQuery,
      lang: _lang,
      fallbackLang
    })

    return result.data
  }

  /**
   * The function returns diceplatform player
   * @param {int} id
   * @returns {Promise<Object>}
   */
  async getDiceplatformPlayer({ id }) {
    this.checkId(id)

    const uri = `diceplatform/player/${id}`

    let result = null

    result = await this.axios.get(uri)

    return result.data
  }

  /**
   * The function returns information about single sport.
   * sb.getSport({ id: 1, withProps: ['matchStatuses', 'categories'], fields: ['id', 'matchStatuses'], withQuery: { categories: { id: 1 } } })
   * @param {String|String[]} [lang]
   * @param {String} [fallbackLang]
   * @param {String[]} withProps - ['matchStatuses', 'categories']
   * @param {Number} id
   * @param {String[]} [fields]=[]
   * @param {Object} [withQuery]={} - example: { markets: { $in: [1, 2, 3] } }
   * @returns {Promise<Object>}
   */
  getSport({ lang, fallbackLang = 'en', withProps = [], id, fields, withQuery = {} }) {
    return this.get({ entityType: 'sports', lang, fallbackLang, withProps, withQuery, id, fields })
  }

  /**
   * The function returns the single category.
   * @param {String} id
   * @param {String[]} [fields]=[]
   * @param {String[]} [withProps]=[]
   * @param {Object} [withQuery]={}
   * @param {String|String[]} [lang]
   * @param {String} [fallbackLang]
   * @returns {Promise<Object>}
   */
  async getCategory({ id, fields, withProps = [], withQuery = {}, lang, fallbackLang = 'en' }) {
    return this.get({
      entityType: 'categories',
      id,
      withProps,
      withQuery,
      fields,
      lang,
      fallbackLang
    })
  }

  /**
   * The function returns the single tournament.
   * @param {String} id
   * @param {String[]} [fields]=[]
   * @param {String|String[]} [lang]
   * @param {String} [fallbackLang]
   * @param {String[]} [withProps]=[]
   * @param {Object} [withQuery]={}
   * @returns {Promise<Object>}
   */
  async getTournament({ id, fields, withProps = [], withQuery = {}, lang, fallbackLang = 'en' }) {
    return this.get({ entityType: 'tournaments', id, withProps, withQuery, fields, lang, fallbackLang })
  }

  async resolveMarkets(sportEvent, lang, fallbackLang) {
    await this.init()
    await this.warmCache([sportEvent])

    if (sportEvent.markets && sportEvent.markets.length) {
      const markets = []

      for (const market of sportEvent.markets) {
        try {
          const resolvedMarketName = await this.resolveMarket({ market, lang, fallbackLang })

          const name = await this.resolveMarketOrOutcomeName({
            name: resolvedMarketName,
            market,
            sportEvent,
            lang,
            fallbackLang
          })

          const outcomes = []

          if (market.outcomes && market.outcomes.length) {
            for (const outcome of market.outcomes) {
              try {
                const resolvedOutcomeName = await this.resolveOutcome({
                  outcomeId: outcome.id,
                  type: outcome.type,
                  competitors: sportEvent.competitors,
                  lang,
                  fallbackLang
                })

                const name = await this.resolveMarketOrOutcomeName({
                  name: resolvedOutcomeName,
                  market,
                  sportEvent,
                  lang,
                  fallbackLang
                })

                outcomes.push({ ...outcome, name })
              } catch (err) {}
            }
          }

          if (!(market.outcomes && market.outcomes.length) || outcomes.length) {
            markets.push({
              ...market,
              name,
              outcomes
            })
          }
        } catch (error) {}
      }

      sportEvent.markets = markets
    }

    return sportEvent
  }

  async warmCache(sportEvents) {
    const idsFetch = {
      markets: new Set(),
      outcomes: new Set(),
      players: new Set()
    }

    sportEvents.forEach(sportEvent => {
      if (sportEvent.markets && sportEvent.markets.length) {
        sportEvent.markets.map(async market => {
          const cachedMarket = this.cache.get(market.id, 'markets')

          if (!cachedMarket) {
            idsFetch.markets.add(market.id)
          }

          if (market.outcomes && market.outcomes.length) {
            market.outcomes.forEach(outcome => {
              switch (outcome.type) {
                case 'predefined':
                  if (!this.cache.get(outcome.id, 'outcomes')) {
                    idsFetch.outcomes.add(outcome.id)
                  }
                  break
                case 'player':
                  if (!this.cache.get(outcome.id, 'players')) {
                    idsFetch.players.add(outcome.id)
                  }
                  break
              }
            })
          }
        })
      }
    })

    if (idsFetch.markets.size) {
      const marketChunks = chunk([...idsFetch.markets], PAGE_SIZE)
      const markets = await Promise.all(
        marketChunks.map(marketChunk =>
          this.findMarkets({
            query: { id: { $in: [...marketChunk] } },
            fields: ['id', 'name'],
            pageSize: PAGE_SIZE
          })
        )
      )

      concat(...markets.map(({ rows }) => rows)).forEach(market =>
        this.cache.set(market.id, { name: market.name }, 'markets', 60)
      )
    }

    if (idsFetch.outcomes.size) {
      const outcomeChunks = chunk([...idsFetch.outcomes], PAGE_SIZE)
      const outcomes = await Promise.all(
        outcomeChunks.map(outcomeChunk =>
          this.findOutcomes({
            query: { id: { $in: [...outcomeChunk] } },
            fields: ['id', 'name'],
            pageSize: PAGE_SIZE
          })
        )
      )

      concat(...outcomes.map(({ rows }) => rows)).forEach(outcome =>
        this.cache.set(outcome.id, { name: outcome.name }, 'outcomes', 60)
      )
    }

    if (idsFetch.players.size) {
      const playerChunks = chunk([...idsFetch.players], PAGE_SIZE)

      const players = await Promise.all(
        playerChunks.map(playerChunk =>
          this.findPlayers({
            query: { id: { $in: [...playerChunk] } },
            fields: ['id', 'name'],
            pageSize: 1000
          })
        )
      )

      concat(...players.map(({ rows }) => rows)).forEach(player =>
        this.cache.set(player.id, { name: player.name }, 'players', 60)
      )
    }
  }

  async resolveMarket({ market, lang, fallbackLang = 'en' }) {
    const cachedMarket = this.cache.get(market.id, 'markets')

    if (cachedMarket) {
      if (lang) {
        return cachedMarket.name[lang] || cachedMarket.name[fallbackLang] || cachedMarket.name.en || ''
      }

      return cachedMarket.name
    }

    const marketInfo = await this.getMarket({ id: market.id, fields: ['id', 'name'] })

    this.cache.set(marketInfo.id, { name: marketInfo.name }, 'markets', 60)

    if (lang) {
      return marketInfo.name[lang] || marketInfo[fallbackLang] || marketInfo.name.en || ''
    }

    return marketInfo.name
  }

  /**
   * The function returns the single market.
   * @param {String} id
   * @param {String[]} [fields]=[]
   * @param {String[]} [withProps]=[]
   * @param {Object} [withQuery]={}
   * @param {String|String[]} [lang]
   * @param {String} [fallbackLang]
   * @returns {Promise<Object>}
   */
  async getMarket({ id, fields, withProps = [], withQuery = {}, lang, fallbackLang = 'en' }) {
    return this.get({
      entityType: 'markets',
      id,
      withProps,
      withQuery,
      fields,
      lang,
      fallbackLang
    })
  }

  /**
   * The function returns the single predefined outcome.
   * @param {String} id
   * @param {String[]} [fields]=[]
   * @param {String[]} [withProps]=[]
   * @param {String|String[]} [lang]
   * @param {String} [fallbackLang]
   * @param {Object} [withQuery]={}
   * @returns {Promise<Object>}
   */
  async getOutcome({ id, fields, withProps = [], withQuery = {}, lang, fallbackLang = 'en' }) {
    return this.get({
      entityType: 'outcomes',
      id,
      withProps,
      withQuery,
      fields,
      lang,
      fallbackLang
    })
  }

  /**
   * The function returns link to statistic of single sport event.
   * sb.getSportEventStatistics({ id: 1 })
   * @param {Number} id
   * @returns {Promise<String>}
   */
  async getSportEventStatistics({ id }) {
    this.checkId(id)

    const uri = `assets/stats/${id}`

    let result = null

    result = await this.axios.get(uri)

    return result.data
  }

  /**
   * The function returns link to statistic of certain competitor.
   * sb.getCompetitorStatistics({ id: 1, competitorId: 1 })
   * @param {Number} id
   * @param {Number} competitorId
   * @returns {Promise<String>}
   */
  async getCompetitorStatistics({ id, competitorId }) {
    this.checkId(id)
    this.checkId(competitorId)

    const uri = `assets/stats/${id}/${competitorId}`

    let result = null

    result = await this.axios.get(uri)

    return result.data
  }

  /**
   * The function returns link to competitor's image.
   * sb.getCompetitorImage({ competitorId: 1, size: 'big' })
   * @param {Number} competitorId
   * @param {String} size - one of ['small', 'medium', 'big']
   * @returns {Promise<String>}
   */
  async getCompetitorImage({ competitorId, size }) {
    this.checkId(competitorId)

    const uri = `assets/images/competitors/${competitorId}.${size}.png`

    let result = null

    result = await this.axios.get(uri)

    return result.data
  }

  async getCompetitor({ id, fields, withQuery = {}, withProps = [], lang }) {
    return this.get({ entityType: 'competitors', withProps, withQuery, id, fields, lang })
  }

  async resolveOutcome({ outcomeId, type = 'predefined', competitors, lang, fallbackLang = 'en' }) {
    let outcome = null
    let player = null

    switch (type) {
      case 'predefined':
      case 'variant':
        outcome = this.cache.get(outcomeId, 'outcomes')

        if (!outcome) {
          outcome = await this.getOutcome({ id: outcomeId, fields: ['id', 'name'] })

          this.cache.set(outcomeId, { name: outcome.name }, 'outcomes', 60)
        }

        break
      case 'player':
        player = this.cache.get(outcomeId, 'players')

        if (!player) {
          player = await this.getPlayer({ id: outcomeId, fields: ['id', 'name'] })

          this.cache.set(outcomeId, { name: player.name }, 'players', 60)
        }

        outcome = player
        break
      case 'competitor':
        outcome = find(competitors, { id: outcomeId })
        break
    }

    if (lang) {
      return outcome.name[lang] || outcome.name[fallbackLang] || outcome.name.en || null
    }

    return outcome.name
  }

  async resolveMarketOrOutcomeName({ name, market, sportEvent, lang, fallbackLang = 'en' }) {
    let specifiers, server, player, player1, player2

    if (name === null) return null

    const getPlayerInfo = async playerId => {
      let player = this.cache.get(playerId, 'players')

      if (!player) {
        player = await this.getPlayer({ id: playerId, fields: ['id', 'name'] })

        this.cache.set(playerId, { name: player.name }, 'players', 60)
      }

      return player
    }

    const replaceName = lang => {
      const pickLang = langObject =>
        lang && typeof langObject === 'object' ? langObject[lang] || langObject[fallbackLang] || null : langObject

      let tmpName = pickLang(name)

      if (specifiers) {
        for (const specifier of Object.keys(specifiers)) {
          let specifierValue = specifiers[specifier]

          if (!specifierValue) {
            continue
          }

          let signs = { plus: '+', minus: '-' }

          if (specifierValue[0] === '-') {
            signs = { plus: '-', minus: '+' }
            specifierValue = specifierValue.slice(1)
          }

          tmpName = tmpName.replace(new RegExp(`{${specifier}}`, 'g'), specifierValue)
          tmpName = tmpName.replace(new RegExp(`{!${specifier}}`, 'g'), specifierValue) // Replace {X} with the ordinal value of the specifier X
          tmpName = tmpName.replace(
            new RegExp(`{!\\(${specifier}\\+(\\d+)\\)}`, 'g'),
            (match, num) => parseInt(specifierValue) + parseInt(num)
          )

          if (specifierValue === '0') {
            tmpName = tmpName.replace(new RegExp(`{\\+${specifier}}`, 'g'), `${specifierValue}`)
            tmpName = tmpName.replace(new RegExp(`{-${specifier}}`, 'g'), `${specifierValue}`)
          } else {
            tmpName = tmpName.replace(new RegExp(`{\\+${specifier}}`, 'g'), `${signs.plus}${specifierValue}`)
            tmpName = tmpName.replace(new RegExp(`{-${specifier}}`, 'g'), `${signs.minus}${specifierValue}`)
          }
        }
      }

      if (server) {
        tmpName = tmpName.replace(/\{%server\}/g, pickLang(server.name))
      }

      if (player) {
        tmpName = tmpName.replace(/\{%player\}/g, pickLang(player.name))
      }

      if (player1) {
        tmpName = tmpName.replace(/\{%player1\}/g, pickLang(player1.name))
      }

      if (player2) {
        tmpName = tmpName.replace(/\{%player2\}/g, pickLang(player2.name))
      }

      // {(X+/-c)} Replace the expression with the value of the specifier X + or - the number c. {!(inningnr+1)} - inningnr=2 - 3rd inning

      // name = name.replace(/{\$event}/g, `"${currLang(sportEvent.name)}"`)

      const replaceValueWithLang = (name, regex, placeholder, lang) => {
        const replaceValue = lang && typeof placeholder === 'object' ? placeholder[lang] : placeholder
        let resultName = null

        const hasPlaceholder = regex.test(name)

        if (!hasPlaceholder) {
          return name
        }

        // if (!replaceValue) {
        //   return null
        // }

        if (replaceValue) {
          resultName = name.replace(regex, replaceValue)
        }

        return resultName
      }

      if (sportEvent.competitors && sportEvent.competitors.length > 1) {
        const homeCompetitor = sportEvent.competitors.filter(c => c.qualifier === 'home')[0].name
        const awayCompetitor = sportEvent.competitors.filter(c => c.qualifier === 'away')[0].name

        tmpName = replaceValueWithLang(tmpName, /{\$competitor1}/g, homeCompetitor, lang)
        tmpName = replaceValueWithLang(tmpName, /{\$competitor2}/g, awayCompetitor, lang)
      }

      if (sportEvent.competitors && specifiers?.competitor) {
        const competitor = sportEvent.competitors.find(c => String(c.id) === specifiers.competitor)

        if (competitor) {
          tmpName = replaceValueWithLang(tmpName, /\{%competitor}/g, competitor.name, lang)
        }
      }

      return tmpName
    }

    if (market.specifiers) {
      specifiers = unpackSpecifiers(market.specifiers) || {}

      if (specifiers.server) {
        server = sportEvent.competitors.find(competitor => String(competitor.id) === String(specifiers.server))
      }

      if (specifiers.player) {
        player = await getPlayerInfo(specifiers.player)
      }

      if (specifiers.player1) {
        player1 = await getPlayerInfo(specifiers.player1)
      }

      if (specifiers.player2) {
        player2 = await getPlayerInfo(specifiers.player2)
      }
    }

    if (typeof name === 'object') {
      const result = {}

      for (const lang of Object.keys(name)) {
        const name = replaceName(lang)

        if (name) {
          result[lang] = name
        }
      }

      return result
    }

    return replaceName(lang)
  }

  websocketListener(url, subscriptions) {
    return new WebsocketListener(url, this.apiKey, subscriptions)
  }
}

module.exports = { SportsbookClientLite, preserveQuotes, unpackSpecifiers }
